import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  computeSeoTags,
  type RobotsDirective,
  type SeoTags,
} from '@/lib/seo';

interface SeoProps {
  title?: string;
  description?: string;
  canonical?: string;
  /** Omit to use the central route policy for explicitly indexable public pages. */
  robots?: RobotsDirective;
  image?: string;
  ogType?: string;
  /** Optional JSON-LD, only for facts already visible on the page. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const MANAGED_ATTR = 'data-applyt-seo';

function ensureMetaByName(name: string): HTMLMetaElement {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    tag.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(tag);
  }
  return tag;
}

function ensureMetaByProperty(property: string): HTMLMetaElement {
  let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    tag.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(tag);
  }
  return tag;
}

function ensureCanonical(): HTMLLinkElement {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute(MANAGED_ATTR, 'true');
    document.head.appendChild(link);
  }
  return link;
}

/** Writes the complete tag set, so no value from a previous route survives. */
export function applySeoTags(tags: SeoTags): void {
  if (typeof document === 'undefined') return;

  document.title = tags.title;
  for (const [name, content] of Object.entries(tags.metaNames)) {
    ensureMetaByName(name).setAttribute('content', content);
  }
  for (const [property, content] of Object.entries(tags.metaProperties)) {
    ensureMetaByProperty(property).setAttribute('content', content);
  }
  ensureCanonical().setAttribute('href', tags.canonical);
}

function applyJsonLd(jsonLd: SeoProps['jsonLd']): () => void {
  if (typeof document === 'undefined') return () => {};
  document
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}]`)
    .forEach((node) => node.remove());
  if (!jsonLd) return () => {};

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute(MANAGED_ATTR, 'true');
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
  return () => script.remove();
}

export function Seo({ title, description, canonical, robots, image, ogType, jsonLd }: SeoProps) {
  const location = useLocation();
  const path = location.pathname;
  const serializedJsonLd = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    applySeoTags(
      computeSeoTags({ path, title, description, canonical, robots, image, ogType }),
    );
  }, [path, title, description, canonical, robots, image, ogType]);

  useEffect(() => applyJsonLd(serializedJsonLd ? JSON.parse(serializedJsonLd) : undefined), [
    serializedJsonLd,
  ]);

  return null;
}
